import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Stack,
  Icon,
  useColorModeValue,
  Badge,
  List,
  ListItem,
  ListIcon,
  SimpleGrid,
  useToast,
  Spinner,
  Tooltip,
} from '@chakra-ui/react';
import { FaCheck, FaTimes, FaMusic, FaSpotify, FaRocket, FaCrown, FaGem } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';

// Load Stripe.js asynchronously
const loadStripe = async (publishableKey: string) => {
  if (!window.Stripe) {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    document.body.appendChild(script);
    await new Promise((resolve) => {
      script.onload = resolve;
    });
  }
  
  return window.Stripe(publishableKey);
};

// Stripe publishable key
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_live_51HJkIcHhxxTvqEX5YGTFxLDfB27nSLcZjsNafdFB9mW6TFETAdhBVnjhnToR3ztnOFuYh7vFrot9BijX9I0Z58Zc00ctnuPXXa';

// Function URL
const CHECKOUT_FUNCTION_URL = import.meta.env.VITE_CHECKOUT_FUNCTION_URL || 'https://worker.syncdit.com/functions/v1/create-checkout-session';

// Stripe price IDs for each plan
const STRIPE_PRICE_IDS: Record<string, string> = {
  monthly: 'price_1RHtvxHhxxTvqEX5ti1fkaHQ',
  yearly: 'price_1RHtwRHhxxTvqEX5J468gEzy',
};

// Define Stripe global type
declare global {
  interface Window {
    Stripe?: any;
  }
}

interface PlanFeature {
  text: string;
  included: boolean;
}

interface PricingPlan {
  id: string;
  title: string;
  price: string;
  originalPrice?: string;
  description: string;
  features: PlanFeature[];
  color: string;
  icon: React.ElementType;
  isPopular?: boolean;
  buttonText: string;
  billingCycle?: string;
  promoCode?: string;
  promoDiscount?: number;
  promoRemaining?: number;
  trialDays?: number;
}

const Pricing = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [userSubscription, setUserSubscription] = useState<any>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  // Check if user is authenticated
  const isAuthenticated = !!user;

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const subTextColor = useColorModeValue('gray.600', 'gray.400');

  // Check if user has an active subscription
  useEffect(() => {
    const checkUserSubscription = async () => {
      if (isAuthenticated && user) {
        try {
          const { data, error } = await supabase
            .from('subscriptions')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          if (error) {
            console.error('Error fetching subscription:', error);
          } else if (data) {
            setUserSubscription(data);
          }
        } catch (err) {
          console.error('Error checking subscription:', err);
        }
      }
      setIsLoading(false);
    };

    checkUserSubscription();
  }, [isAuthenticated, user]);

  // Handle purchase with Stripe integration
  const handlePurchase = async (plan: PricingPlan) => {
    if (!isAuthenticated && plan.id !== 'free') {
      // Redirect to signup page if not authenticated
      navigate('/signup?tab=0&redirect=pricing');
      return;
    }

    // Free plan doesn't need checkout
    if (plan.id === 'free') {
      navigate('/player');
      return;
    }

    try {
      setIsLoading(true);
      
      // Get the correct price ID based on the plan
      const priceId = STRIPE_PRICE_IDS[plan.id];
      
      if (!priceId) {
        throw new Error(`No price ID configured for plan: ${plan.id}`);
      }
      
      // Load Stripe.js
      const stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY);
      if (!stripe) {
        throw new Error('Failed to load Stripe');
      }
      
      // Call our Supabase Edge Function to create a checkout session
      const response = await fetch(CHECKOUT_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId,
          userId: user?.id,
          customerEmail: user?.email,
          planId: plan.id,
          promoCode: plan.promoCode,
          
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }
      
      const { sessionId } = await response.json();
      
      // Redirect to Stripe Checkout
      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        throw error;
      }
    } catch (error: any) {
      console.error('Error starting checkout:', error);
      toast({
        title: 'Checkout Error',
        description: error?.message || 'An error occurred during checkout',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const pricingPlans: PricingPlan[] = [
    {
      id: 'free',
      title: 'Free',
      price: '$0',
      description: 'Get started with basic features',
      color: 'gray',
      icon: FaMusic,
      buttonText: 'Get Started',
      features: [
        { text: 'Limited access to apps and games', included: true },
        { text: 'Enable fullscreen mode', included: true },
        { text: 'Access to lyrics player', included: false },
        { text: 'Pin games and theatre apps', included: false },
      ],
    },
    {
      id: 'monthly',
      title: 'SyncdIt Pro Monthly',
      price: '$3.99',
      originalPrice: '$3.99',
      description: 'Perfect for individual users',
      color: 'blue',
      icon: FaSpotify,
      isPopular: true,
      buttonText: 'Subscribe Now',
      billingCycle: 'month',
      promoCode: 'TTLIFETIME50',
      promoDiscount: 2.00,
      promoRemaining: 200,
      trialDays: 7,
      features: [
        { text: 'Priority access to Lyrics Player', included: true },
        { text: 'Pin favorite games and theatre apps', included: true },
        { text: 'Customize and create lyric templates', included: true },
        { text: 'Request lyrics for songs', included: true },
        { text: 'Access to all games and entertainment', included: true },
        { text: 'Request new features', included: true },
        { text: 'Faster ticket response times', included: true },
      ],
    },
    {
      id: 'yearly',
      title: 'SyncdIt Pro Yearly',
      price: '$39.99',
      originalPrice: '$39.99',
      description: 'Best value for committed users',
      color: 'purple',
      icon: FaRocket,
      buttonText: 'Subscribe Now',
      billingCycle: 'year',
      promoCode: 'TTLIFETIME50',
      promoDiscount: 20.00,
      promoRemaining: 100,
      trialDays: 7,
      features: [
        { text: 'Everything included in Pro Monthly', included: true },
        { text: 'Get 2 months free with yearly billing', included: true },
        { text: 'Early access to new features', included: true },
        { text: 'Prioritized feature requests', included: true },
      ],
    },
  ];

  // Update the PricingPlan render function to use custom logo images with enhanced styling
  const PlanCard = ({ plan }: { plan: PricingPlan }) => {
    // Check if user already has this plan
    const isCurrentPlan = userSubscription?.plan_id === plan.id;

    return (
      <Box
        position="relative"
        borderRadius="2xl"
        overflow="hidden"
        transform={plan.isPopular ? { md: 'scale(1.05)' } : 'none'}
        zIndex={plan.isPopular ? 1 : 0}
        transition="all 0.3s"
        className="glass-card"
        borderWidth="1px"
        borderColor={plan.isPopular ? `${plan.color}.900` : 'rgba(255, 255, 255, 0.1)'}
        bg="rgba(0, 0, 0, 0.5)"
        backdropFilter="blur(10px)"
        boxShadow={plan.isPopular ? `0 0 25px rgba(66, 153, 225, 0.3)` : "0 8px 20px rgba(0, 0, 0, 0.2)"}
        _hover={{ 
          transform: plan.isPopular 
            ? { md: 'scale(1.08)' } 
            : 'translateY(-10px)',
          boxShadow: 'xl'
        }}
      >
        {plan.isPopular && (
          <Badge
            position="absolute"
            top={4}
            right={4}
            colorScheme="blue"
            variant="solid"
            borderRadius="full"
            px={3}
            py={1}
            fontSize="xs"
            fontWeight="bold"
          >
            Most Popular
          </Badge>
        )}

        <Box p={8}>
          <VStack align="start" spacing={6}>
            <HStack>
              <Box
                borderRadius="xl"
                bg={`${plan.color}.900`}
                color="white"
                width="70px"
                p="2"
                height="70px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                overflow="visible"
                position="relative"
                boxShadow={`inset 0 0 20px ${plan.id !== 'free' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.3)'}`}
              >
                {plan.id === 'free' ? (
                  // Free plan icon with proper path
                  <img 
                    src="/assets/logos/logo_only.png"
                    alt="Free Plan" 
                    style={{ width: '60px', height: '60px', objectFit: 'contain' }} 
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const iconElement = document.createElement('div');
                      iconElement.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"></path></svg>';
                      e.currentTarget.parentNode?.appendChild(iconElement);
                    }}
                  />
                ) : (
                  // Pro plan icon as badge overlay on regular logo
                  <Box position="relative" width="100%" height="100%">
                    <img 
                      src="/assets/logos/logo_only.png"
                      alt="Pro Plan" 
                      style={{ width: '100%', height: '100%', objectFit: 'contain', filter: 'brightness(1.2) contrast(1.1)' }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const iconElement = document.createElement('div');
                        iconElement.innerHTML = plan.id === 'monthly' 
                          ? '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M5 16L3 5L8.5 10L12 4L15.5 10L21 5L19 16H5M19 19C19 19.6 18.6 20 18 20H6C5.4 20 5 19.6 5 19V18H19V19Z"></path></svg>'
                          : '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M6,16L3,5L8.5,10L12,4L15.5,10L21,5L18,16H6M15,18A3,3 0 0,1 12,21A3,3 0 0,1 9,18"></path></svg>';
                        e.currentTarget.parentNode?.appendChild(iconElement);
                      }}
                    />
                    {/* Pro Badge */}
                    <Badge
                      position="absolute"
                      top="-18px"
                      right="-18px"
                      bg={plan.id === 'monthly' ? 'blue.500' : 'purple.600'}
                      color="white"
                      fontSize='xs'
                      fontWeight="extrabold"
                      py={1}
                      px={2}
                      borderRadius="full"
                      boxShadow={`0 4px 12px ${plan.id === 'monthly' ? 'rgba(66, 153, 225, 0.8)' : 'rgba(128, 90, 213, 0.8)'}`}
                      textTransform="uppercase"
                      letterSpacing="wider"
                      border="2px solid"
                      borderColor={plan.id === 'monthly' ? 'blue.200' : 'purple.300'}
                      zIndex={10}
                      animation="pulse 2s infinite"
                      sx={{
                        '@keyframes pulse': {
                          '0%': {
                            boxShadow: `0 4px 12px ${plan.id === 'monthly' ? 'rgba(66, 153, 225, 0.8)' : 'rgba(128, 90, 213, 0.8)'}`,
                          },
                          '50%': {
                            boxShadow: `0 4px 30px ${plan.id === 'monthly' ? 'rgba(66, 153, 225, 1)' : 'rgba(128, 90, 213, 1)'}`,
                          },
                          '100%': {
                            boxShadow: `0 4px 12px ${plan.id === 'monthly' ? 'rgba(66, 153, 225, 0.8)' : 'rgba(128, 90, 213, 0.8)'}`,
                          },
                        },
                      }}
                    >
                      {'PRO'}
                    </Badge>
                  </Box>
                )}
              </Box>
              <VStack align="start" spacing={0}>
                <Text fontSize="xl" fontWeight="bold">
                  {plan.title}
                </Text>
                <Text fontSize="sm" color="gray.400">
                  {plan.description}
                </Text>
              </VStack>
            </HStack>

            {/* Price section with enhanced promo display */}
            <VStack align="baseline" spacing={2} width="100%">
              {plan.promoDiscount && (
                <HStack width="100%" justify="space-between">
                  <Box>
                    {plan.promoRemaining && (
                      <Badge 
                        colorScheme="red" 
                        variant="solid" 
                        fontSize="sm" 
                        py={1} 
                        px={2} 
                        borderRadius="md"
                      >
                        LIMITED TIME OFFER
                      </Badge>
                    )}
                  </Box>
                  {plan.promoRemaining && plan.promoRemaining < 50 && (
                    <Badge 
                      colorScheme="yellow" 
                      variant="solid" 
                      fontSize="sm" 
                      py={1} 
                      px={2} 
                      borderRadius="md"
                    >
                      ONLY {plan.promoRemaining} LEFT
                    </Badge>
                  )}
                </HStack>
              )}
              
              {plan.promoDiscount ? (
                <VStack align="start" spacing={1} width="100%">
                  <HStack align="baseline" spacing={2}>
                    <Text 
                      as="s" 
                      color="red.500" 
                      fontSize="xl" 
                      fontWeight="bold"
                    >
                      {plan.originalPrice}
                    </Text>
                    <Badge colorScheme="red" variant="solid">SAVE ${plan.promoDiscount.toFixed(2)}</Badge>
                  </HStack>
                  <HStack align="baseline" spacing={2}>
                    <Text fontSize="5xl" fontWeight="bold">
                      ${(parseFloat(plan.price.substring(1)) - plan.promoDiscount).toFixed(2)}
                    </Text>
                    {plan.price !== '$0' && (
                      <Text color="gray.400" fontSize="lg" alignSelf="flex-end">
                        /{plan.billingCycle}
                      </Text>
                    )}
                  </HStack>
                  {plan.promoCode && (
                    <Box 
                      bg="blackAlpha.300"
                      px={3}
                      py={2}
                      borderRadius="md" 
                      borderWidth="1px"
                      borderColor="red.500"
                      borderStyle="dashed"
                      mt={1}
                      width="100%"
                    >
                      <HStack>
                        <Text fontSize="sm" color="yellow.300" fontWeight="bold">
                          Use code <Text as="span" fontWeight="extrabold" color="white" bg="blackAlpha.400" px={2} py={1} borderRadius="md" letterSpacing="wide">{plan.promoCode}</Text> at checkout
                        </Text>
                      </HStack>
                    </Box>
                  )}
                </VStack>
              ) : (
                <HStack align="baseline" spacing={2}>
                  <Text fontSize="5xl" fontWeight="bold">
                    {plan.price}
                  </Text>
                  {plan.price !== '$0' && (
                    <Text color="gray.400" fontSize="lg" alignSelf="flex-end">
                      /{plan.billingCycle}
                    </Text>
                  )}
                </HStack>
              )}
            </VStack>

            {plan.trialDays && (
              <Text color="green.400" fontWeight="bold">
                {plan.trialDays}-day free trial
              </Text>
            )}

            <VStack align="start" spacing={3} width="100%">
              {plan.features.map((feature, index) => (
                <HStack key={index} width="100%" spacing={3} align="flex-start">
                  <Box 
                    borderRadius="full" 
                    bg={feature.included ? `${plan.color}.500` : 'gray.600'} 
                    color="white"
                    width="24px"
                    height="24px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Icon 
                      as={feature.included ? FaCheck : FaTimes} 
                      boxSize={3} 
                    />
                  </Box>
                  {feature.text.includes('Everything included in Pro') ? (
                    <Tooltip
                      hasArrow
                      label={
                        <VStack align="start" p={2} spacing={1}>
                          <Text fontWeight="bold" mb={1}>Pro Monthly Features:</Text>
                          {pricingPlans.find(p => p.id === 'monthly')?.features.map((f, i) => (
                            <Text key={i} fontSize="sm">• {f.text}</Text>
                          ))}
                        </VStack>
                      }
                      bg="gray.800"
                      color="white"
                      placement="top"
                      openDelay={300}
                      maxW="300px"
                    >
                      <Text 
                        color="white"
                        fontSize="md"
                        fontWeight="bold"
                        textDecoration="underline"
                        textDecorationColor={`${plan.color}.400`}
                        textUnderlineOffset="4px"
                        cursor="help"
                      >
                        {feature.text}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text 
                      color={feature.included ? 'white' : 'gray.400'}
                      fontSize="md"
                    >
                      {feature.text}
                    </Text>
                  )}
                </HStack>
              ))}
            </VStack>

            <Button
              width="100%"
              colorScheme={plan.color}
              size="lg"
              onClick={() => handlePurchase(plan)}
              mt={4}
              variant={plan.id === 'free' ? 'outline' : 'solid'}
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: 'lg',
              }}
              isDisabled={isCurrentPlan}
            >
              {isCurrentPlan ? 'Current Plan' : plan.buttonText}
            </Button>
          </VStack>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      minH="100vh"
      bgGradient="linear(to-b, gray.900, black)"
      color="white"
      position="relative"
      overflow="hidden"
    >
      <AppHeader pageTitle="Pricing" />

      {/* Background Effects */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bgGradient="radial(circle at 20% 20%, rgba(29, 185, 84, 0.1), transparent 40%)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bgGradient="radial(circle at 80% 80%, rgba(66, 153, 225, 0.1), transparent 40%)"
        pointerEvents="none"
      />

      <Container maxW="container.xl" py={20} position="relative" mt="72px">
        <VStack spacing={16} align="stretch">
          {/* Header Section */}
          <VStack spacing={6} textAlign="center">
            <Heading
              fontSize={{ base: "4xl", md: "6xl" }}
              fontWeight="extrabold"
              bg="white"
              bgClip="text"
              letterSpacing="tight"
            >
              Choose Your Plan
            </Heading>
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              color="gray.400"
              maxW="2xl"
              mx="auto"
            >
              Unlock the full potential of SyncdIt with our premium plans designed for every music lover.
            </Text>
          </VStack>

          {isLoading ? (
            <Flex justify="center" align="center" minH="300px">
              <Spinner size="xl" color="blue.400" thickness="4px" />
            </Flex>
          ) : (
            <>
              {userSubscription && userSubscription.status === 'active' && (
                <Box
                  p={6}
                  borderRadius="xl"
                  bg="green.800"
                  borderWidth="1px"
                  borderColor="green.600"
                  textAlign="center"
                >
                  <Heading size="md" mb={3}>
                    You're currently subscribed to the {userSubscription.plan_name} plan
                  </Heading>
                  <Text>
                    Your subscription renews on {new Date(userSubscription.next_bill_date).toLocaleDateString()}
                  </Text>
                  <Button
                    mt={4}
                    size="sm"
                    colorScheme="green"
                    variant="outline"
                    onClick={() => navigate('/settings')}
                  >
                    Manage Subscription
                  </Button>
                </Box>
              )}
              
              {/* Pricing Plans */}
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                {pricingPlans.map((plan) => (
                  <PlanCard key={plan.title} plan={plan} />
                ))}
              </SimpleGrid>
            </>
          )}

          {/* Guarantee Section */}
          <Box 
            p={8} 
            borderRadius="xl"
            borderWidth="1px"
            borderColor="whiteAlpha.200"
            bg="whiteAlpha.100"
            mt={16}
          >
            <Stack 
              direction={{ base: 'column', md: 'row' }}
              align="center" 
              spacing={6}
            >
              <Box flex="1">
                <Heading as="h3" size="lg" mb={2}>
                  100% Satisfaction Guarantee
                </Heading>
                <Text color="gray.400">
                  Try SyncdIt Pro risk-free. If you're not completely satisfied, contact us within 30 days for a full refund. No questions asked.
                </Text>
              </Box>
            </Stack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default Pricing; 